<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  HIDDEN_PERSONAL_CONNECTION_TYPES,
  TEAM_SCOPED_CONNECTION_TYPES,
  normalizeAceleresConnectionType,
} from 'dashboard/constants/aceleresConnectionTypes';

const props = defineProps({
  inboxes:         { type: Array,   default: () => [] },
  labels:          { type: Array,   default: () => [] },
  agents:          { type: Array,   default: () => [] },
  teams:           { type: Array,   default: () => [] },
  showInboxFilter: { type: Boolean, default: true      },
});

const emit = defineEmits(['apply', 'clear', 'count-change', 'openStateChange']);

const accountId = useMapGetter('getCurrentAccountId');
const route = useRoute();
const storageKey = computed(
  () => `aceleres_conv_filter_${accountId.value || 'default'}`
);

// ─── state ──────────────────────────────────────────────────────────────────
// multi-selecao: cada chave guarda um ARRAY de ids selecionados
const EMPTY = () => ({
  canal: [], etiqueta: [], usuario: [], equipe: [],
  status: [], prioridade: [],
  ia: false,
});
const sel = ref(EMPTY());
const showModal = ref(false);
// 'centered' = modal classico (botao "Filtros" da barra de chips).
// 'slidePanel' = painel desliza por dentro do container do ChatList (icone
// filter da pill bar) — overlay total da lista com header e seta de voltar.
// 'popover' = card flutuante ancorado ao botao de filtro da pill bar.
const modalMode = ref('centered');
const popoverStyle = ref({});
const popoverRef = ref(null);
const popoverTriggerEl = ref(null);

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const openKey = ref(null); // secao expandida do acordeao (uma por vez)
const search = ref({
  canal: '', etiqueta: '', usuario: '', equipe: '',
  status: '', prioridade: '',
});

const longOptionsListClass = 'max-h-16 overflow-y-auto overscroll-contain pr-1 acl-hide-scroll';

// Opcoes fixas pras secoes Status e Prioridade (atributos nativos do
// Chatwoot — bate com Conversation#status e #priority).
const STATUS_OPTS = [
  { id: 'open',     label: 'Abertas' },
  { id: 'resolved', label: 'Concluído' },
  { id: 'pending',  label: 'Pendentes' },
  { id: 'snoozed',  label: 'Adiadas' },
];
const PRIORIDADE_OPTS = [
  { id: 'urgent', label: 'Urgente' },
  { id: 'high',   label: 'Alta' },
  { id: 'medium', label: 'Média' },
  { id: 'low',    label: 'Baixa' },
];
// toggle "Apenas minhas equipes" — APENAS VISUAL por enquanto (simulado,
// sem efeito no filtro; pedido do CTO 2026-06-11)
const onlyMyTeams = ref(false);

let applyTimer = null;

// ─── icones de canal (brand) ────────────────────────────────────────────────
const CHANNEL_ICONS = {
  [INBOX_TYPES.WHATSAPP]:  { icon: 'i-ri-whatsapp-fill',  color: '#25D366' },
  // Channel::Api = instancias Evolution -> WhatsApp corporativo
  [INBOX_TYPES.API]:       { icon: 'i-ri-whatsapp-fill',  color: '#25D366' },
  [INBOX_TYPES.FB]:        { icon: 'i-ri-messenger-fill', color: '#0084FF' },
  [INBOX_TYPES.INSTAGRAM]: { icon: 'i-ri-instagram-fill', color: '#E1306C' },
  [INBOX_TYPES.TELEGRAM]:  { icon: 'i-ri-telegram-fill',  color: '#229ED9' },
  [INBOX_TYPES.EMAIL]:     { icon: 'i-ri-mail-fill',      color: '#64748B' },
  [INBOX_TYPES.WEB]:       { icon: 'i-ri-global-fill',    color: '#64748B' },
  [INBOX_TYPES.VOICE]:     { icon: 'i-ri-phone-fill',     color: '#64748B' },
};
const DEFAULT_CHANNEL_ICON = { icon: 'i-ri-chat-1-fill', color: '#94A3B8' };
function channelIconFor(inbox) {
  return CHANNEL_ICONS[inbox.channel_type] || DEFAULT_CHANNEL_ICON;
}

function channelAdditionalAttributes(inbox) {
  return inbox?.additional_attributes || inbox?.additionalAttributes || {};
}

function cleanChannelLabel(name) {
  return (name || '')
    .replace(/ · [a-f0-9]{6}$/i, '')
    .replace(/\s*[([{]?\s*vo[cç]ê\s*[)\]}]?$/i, '')
    .trim();
}

function normalizedText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function isSyntheticAceleresText(value) {
  const text = normalizedText(value);
  return (
    text.includes('ia aceleres') ||
    text.includes('sistema aceleres') ||
    text.includes('integracao aceleres')
  );
}

function isSyntheticAceleresEmail(value) {
  const email = normalizedText(value);
  return (
    email.startsWith('system+company') ||
    email === 'evolution-bot@aceleres.ai' ||
    email === 'sistema-aceleres@aceleres.ai'
  );
}

function isSyntheticAceleresAgent(agent) {
  return (
    isSyntheticAceleresText(agent?.name) ||
    isSyntheticAceleresEmail(agent?.email)
  );
}

// Aceleres: instancias pessoais de WhatsApp (numero do gestor/vendedor) NAO sao
// "canais" — saem do filtro Canais; o consultor continua filtravel pela secao
// Usuarios. connection_type vem em additional_attributes do Channel::Api.
// Usuário logado — usado na exceção de dono abaixo.
const currentUserId = useMapGetter('getCurrentUserID');
const currentUser = useMapGetter('getCurrentUser');

function uniqueNumericIds(ids) {
  return [...new Set(ids.map(id => Number(id)).filter(id => !Number.isNaN(id)))];
}

function teamIdsFrom(teams = []) {
  return uniqueNumericIds((teams || []).map(team => team?.id));
}

const currentUserTeamIds = computed(() => {
  const uid = Number(currentUserId.value || currentUser.value?.id);
  const currentAgent = (props.agents || []).find(a => Number(a.id) === uid);
  const directTeams = [
    ...teamIdsFrom(currentAgent?.teams),
    ...teamIdsFrom(currentUser.value?.teams),
    ...teamIdsFrom(
      (props.teams || []).filter(team => team?.is_member || team?.is_supervisor)
    ),
  ];

  return uniqueNumericIds(directTeams);
});

const agentTeamIdsByUserId = computed(() => {
  return (props.agents || []).reduce((map, agent) => {
    map.set(Number(agent.id), teamIdsFrom(agent.teams));
    return map;
  }, new Map());
});

function inboxTeamId(inbox) {
  const id = inbox?.team_id ?? inbox?.teamId;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? null : numeric;
}

function inboxConnectionType(inbox) {
  return normalizeAceleresConnectionType(channelAdditionalAttributes(inbox)?.connection_type);
}

function inboxOwnerId(inbox) {
  const owner = Number(channelAdditionalAttributes(inbox)?.owner_chatwoot_user_id);
  return owner > 0 ? owner : null;
}

function inboxOwnerAgent(inbox) {
  const ownerId = inboxOwnerId(inbox);
  if (!ownerId) return null;
  return (props.agents || []).find(agent => Number(agent.id) === ownerId) || null;
}

function isSyntheticAceleresInbox(inbox) {
  const attrs = channelAdditionalAttributes(inbox);
  return (
    isSyntheticAceleresText(inbox?.name) ||
    isSyntheticAceleresText(attrs?.owner_name) ||
    isSyntheticAceleresEmail(attrs?.owner_email) ||
    isSyntheticAceleresAgent(inboxOwnerAgent(inbox))
  );
}

function inboxOwnerTeamIds(inbox) {
  const attrs = channelAdditionalAttributes(inbox);
  return uniqueNumericIds(
    attrs?.aceleres_owner_team_ids ||
      attrs?.owner_team_ids ||
      attrs?.ownerTeamIds ||
      []
  );
}

function ownerBelongsToCurrentTeams(ownerId, inbox) {
  if (!ownerId) return true;
  if (ownerId === Number(currentUserId.value)) return true;
  if (!currentUserTeamIds.value.length) return true;

  const serializedOwnerTeamIds = inboxOwnerTeamIds(inbox);
  const ownerTeamIds =
    serializedOwnerTeamIds.length > 0
      ? serializedOwnerTeamIds
      : agentTeamIdsByUserId.value.get(ownerId) || [];

  // O backend já escopa as instâncias. Se a UI ainda não carregou os times do
  // agente, não esconda um canal que a API autorizou.
  if (!ownerTeamIds.length) return true;

  return ownerTeamIds.some(teamId => currentUserTeamIds.value.includes(teamId));
}

function isTeamScopedInboxVisible(inbox) {
  const teamId = inboxTeamId(inbox);
  if (teamId) {
    if (!currentUserTeamIds.value.length) return true;
    return currentUserTeamIds.value.includes(teamId);
  }

  if (TEAM_SCOPED_CONNECTION_TYPES.includes(inboxConnectionType(inbox))) {
    return ownerBelongsToCurrentTeams(inboxOwnerId(inbox), inbox);
  }

  return true;
}
// Aceleres: instância pessoal (individual_own) sai do filtro Canais — EXCETO a do
// próprio dono. O gestor/vendedor precisa enxergar a própria linha pra filtrar
// conversas por ela. Espelha a regra do backend (lib/aceleres/inbox_policy_scope_override.rb:
// "o dono sempre vê a própria"). owner_chatwoot_user_id vem em additional_attributes.
function isIndividualInstance(inbox) {
  const attrs = channelAdditionalAttributes(inbox);
  const connectionType = inboxConnectionType(inbox);
  if (!HIDDEN_PERSONAL_CONNECTION_TYPES.includes(connectionType)) return false;
  const owner = Number(attrs?.owner_chatwoot_user_id);
  // dono logado → não esconde (vê a própria linha pessoal); demais → esconde
  return !(owner > 0 && owner === Number(currentUserId.value));
}
// Aceleres: separacao de canais por status oficial.
// Oficiais   = WhatsApp Cloud API Meta (Channel::Whatsapp)
// NaoOficiais = Channel::Api (Evolution / Baileys) — instancias corporativas
// Outros     = resto (Instagram, Facebook, Email, WebWidget, Telegram, etc.)
const CHANNEL_GROUPS = [
  { key: 'oficial',     title: 'Oficiais' },
  { key: 'naoOficial',  title: 'Não oficiais' },
  { key: 'outros',      title: 'Outros' },
];
function channelGroupFor(channelType) {
  if (channelType === INBOX_TYPES.WHATSAPP) return 'oficial';
  if (channelType === INBOX_TYPES.API)      return 'naoOficial';
  return 'outros';
}
// Sort estavel pt-BR (acentos nao bagunçam ordem)
function sortByLabelPtBr(a, b) {
  return (a.label || '').localeCompare(b.label || '', 'pt-BR', { sensitivity: 'base' });
}

// ─── secoes ─────────────────────────────────────────────────────────────────
const FILTERS = computed(() => {
  // Canais: ordenar alfabeticamente + agrupar por status oficial.
  // Mantemos opts plano (uniao ja ordenada) para compatibilidade com
  // optsFor / labelFor / activeChips / buildConditions / persist.
  const canalItems = props.inboxes
    .filter(isTeamScopedInboxVisible)
    .filter(i => !isSyntheticAceleresInbox(i))
    .filter(i => !isIndividualInstance(i))
    .map(i => ({
      id: i.id,
      label: cleanChannelLabel(i.name),
      channel: channelIconFor(i),
      _group: channelGroupFor(i.channel_type),
    }))
    .sort(sortByLabelPtBr);
  const canalGroups = CHANNEL_GROUPS
    .map(g => ({ key: g.key, title: g.title, items: canalItems.filter(o => o._group === g.key) }))
    .filter(g => g.items.length);

  const all = [
    { key: 'canal', label: 'Canais', icon: 'i-lucide-arrow-left-right',
      opts: canalItems, groups: canalGroups },
    { key: 'etiqueta', label: 'Etiquetas', icon: 'i-lucide-tag',
      opts: props.labels.map(l => ({ id: l.title, label: l.title, color: l.color })) },
    { key: 'usuario', label: 'Usuários', icon: 'i-lucide-users',
      opts: props.agents.filter(a => !isSyntheticAceleresAgent(a)).map(a => ({ id: a.id, label: a.name })) },
    { key: 'equipe', label: 'Equipes', icon: 'i-lucide-archive',
      opts: props.teams.map(t => ({ id: t.id, label: t.name })) },
    { key: 'status', label: 'Status', icon: 'i-lucide-circle-dot',
      opts: STATUS_OPTS },
    { key: 'prioridade', label: 'Prioridade', icon: 'i-lucide-flag',
      opts: PRIORIDADE_OPTS },
  ];
  return props.showInboxFilter ? all : all.filter(f => f.key !== 'canal');
});

// Filtra items de um grupo pelo termo de busca da secao.
function groupItemsFor(f, g) {
  const term = (search.value[f.key] || '').toLowerCase().trim();
  if (!term) return g.items;
  return g.items.filter(o => (o.label || '').toLowerCase().includes(term));
}
// Numero total de grupos nao vazios apos aplicar o filtro de busca.
function nonEmptyGroupCount(f) {
  return (f.groups || []).reduce((n, g) => n + (groupItemsFor(f, g).length ? 1 : 0), 0);
}

const SECTION_KEYS = ['canal', 'etiqueta', 'usuario', 'equipe', 'status', 'prioridade'];
const activeCount = computed(
  () =>
    SECTION_KEYS.reduce((n, k) => n + sel.value[k].length, 0) +
    (sel.value.ia ? 1 : 0)
);
const hasActiveFilters = computed(() => activeCount.value > 0);
// Aceleres: emite o nº de filtros selecionados pro pai (ChatList → ChatTypeTabs)
// alimentar o badge do botão de filtro. immediate:true cobre o estado inicial.
watch(activeCount, n => emit('count-change', n), { immediate: true });

function optsFor(f) {
  const term = (search.value[f.key] || '').toLowerCase().trim();
  if (!term) return f.opts;
  return f.opts.filter(o => (o.label || '').toLowerCase().includes(term));
}
function labelFor(f, id) {
  const o = f.opts.find(x => x.id === id);
  return o ? o.label : id;
}
const activeChips = computed(() => {
  const chips = FILTERS.value.flatMap(f =>
    sel.value[f.key].map(id => ({
      key: f.key, id, icon: f.icon, label: labelFor(f, id),
    }))
  );
  if (sel.value.ia) {
    chips.push({ key: 'ia', id: '__ia__', icon: 'i-lucide-bot', label: 'Atendidos pela IA' });
  }
  return chips;
});

// ─── modal / acordeao ───────────────────────────────────────────────────────
function computePopoverStyle(triggerEl = popoverTriggerEl.value) {
  const margin = 12;

  if (window.innerWidth < 1024 && route.name === 'home') {
    const top = 245.562;
    return {
      top: `${top}px`,
      left: '78px',
      width: '388px',
    };
  }

  if (window.innerWidth >= 1024) {
    const top = 170.771;
    return {
      top: `${top}px`,
      left: '532.802px',
      width: '420px',
    };
  }

  const gap = 12;
  const viewportWidth = window.innerWidth;
  const width = Math.min(420, viewportWidth - margin * 2);
  const rect = triggerEl?.getBoundingClientRect?.();
  if (!rect) {
    return {
      top: `${margin}px`,
      left: `${margin}px`,
      width: `${width}px`,
    };
  }

  const left = clamp(
    rect.right - width + 8,
    margin,
    viewportWidth - width - margin
  );
  const top = rect.bottom + gap;

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
}

function openModal(mode = 'centered', triggerEl = null) {
  let nextMode = 'centered';
  if (mode === 'slidePanel') nextMode = 'slidePanel';
  else if (mode === 'popover') nextMode = 'popover';

  if (
    showModal.value &&
    nextMode === 'popover' &&
    modalMode.value === 'popover' &&
    popoverTriggerEl.value === triggerEl
  ) {
    showModal.value = false;
    emit('openStateChange', false);
    return;
  }

  search.value = {
    canal: '',
    etiqueta: '',
    usuario: '',
    equipe: '',
    status: '',
    prioridade: '',
  };
  openKey.value = null;
  modalMode.value = nextMode;
  if (modalMode.value === 'popover') {
    popoverTriggerEl.value = triggerEl;
    popoverStyle.value = computePopoverStyle(triggerEl);
  }
  emit('openStateChange', true);
  showModal.value = true;
}
function closeModal() {
  showModal.value = false;
  emit('openStateChange', false);
}
function toggleSection(key) {
  openKey.value = openKey.value === key ? null : key;
}

function isChecked(key, id) {
  return sel.value[key].includes(id);
}
function toggleItem(key, id) {
  const arr = sel.value[key];
  const i = arr.indexOf(id);
  if (i === -1) arr.push(id);
  else arr.splice(i, 1);
  scheduleApply();
}
function clearSection(key) {
  sel.value[key] = [];
  applyNow();
}
// filtro "Atendidos pela IA" — usa o flag custom_attributes.atendido_ia
// (NUNCA etiqueta; decisao CTO 2026-06-11). Funcional, aplica na hora.
function toggleIa() {
  sel.value.ia = !sel.value.ia;
  applyNow();
}

// ─── conditions (formato advanced filters do Chatwoot) ─────────────────────
function buildConditions(src) {
  const c = [];
  if (props.showInboxFilter && src.canal.length) {
    c.push({ attributeKey: 'inbox_id', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.canal.map(id => ({
        id, name: props.inboxes.find(i => i.id === id)?.name ?? '',
      })) });
  }
  if (src.etiqueta.length) {
    c.push({ attributeKey: 'labels', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.etiqueta.map(t => ({ id: t, name: t })) });
  }
  if (src.usuario.length) {
    c.push({ attributeKey: 'assignee_id', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.usuario.map(id => ({
        id, name: props.agents.find(a => a.id === id)?.name ?? '',
      })) });
  }
  if (src.equipe.length) {
    c.push({ attributeKey: 'team_id', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.equipe.map(id => ({
        id, name: props.teams.find(t => t.id === id)?.name ?? '',
      })) });
  }
  if (src.status.length) {
    c.push({ attributeKey: 'status', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.status.map(id => ({
        id, name: STATUS_OPTS.find(o => o.id === id)?.label ?? id,
      })) });
  }
  if (src.prioridade.length) {
    c.push({ attributeKey: 'priority', filterOperator: 'equal_to', queryOperator: 'and',
      values: src.prioridade.map(id => ({
        id, name: PRIORIDADE_OPTS.find(o => o.id === id)?.label ?? id,
      })) });
  }
  if (src.ia) {
    // custom attribute "atendido_ia" (CustomAttributeDefinition conta 38) —
    // filtra pelo flag setado pelo sync da Aurora, nao por etiqueta
    // values: [true] (boolean) — o flag no JSON da conversa é boolean; o
    // re-filtro client-side (filterHelpers.matchesFilters) compara por
    // inclusão estrita, então ['true'] (string) zerava a lista após o fetch.
    c.push({ attributeKey: 'atendido_ia', filterOperator: 'equal_to',
      queryOperator: 'and', values: [true] });
  }
  return c;
}

function persist() {
  try { localStorage.setItem(storageKey.value, JSON.stringify(sel.value)); } catch (e) { /* noop */ }
}
function emitFor(src) {
  const c = buildConditions(src);
  emit(c.length === 0 ? 'clear' : 'apply', c.length === 0 ? undefined : c);
  return c.length > 0;
}
// aplicacao automatica (sem botao "Aplicar"): debounce curto para nao disparar
// uma busca por clique enquanto o usuario marca varios checkboxes em sequencia
function scheduleApply() {
  if (applyTimer) clearTimeout(applyTimer);
  applyTimer = setTimeout(applyNow, 600);
}
function applyNow() {
  if (applyTimer) { clearTimeout(applyTimer); applyTimer = null; }
  persist();
  emitFor(sel.value);
}

// remove 1 item pela chip da barra
function removeOne(key, id) {
  if (key === 'ia') {
    sel.value.ia = false;
  } else {
    const arr = sel.value[key];
    const i = arr.indexOf(id);
    if (i !== -1) arr.splice(i, 1);
  }
  applyNow();
}
// limpa tudo (tambem exposto pro ChatList)
function clearAll(emitClear = true) {
  if (applyTimer) { clearTimeout(applyTimer); applyTimer = null; }
  sel.value = EMPTY();
  persist();
  // emitClear=false: chamada imperativa do ChatList (resetAndFetchData).
  // Emitir aqui criaria recursao mutua clear -> resetAndFetchData -> clearAll.
  // (clique do botao passa MouseEvent, que e truthy -> emite normalmente)
  if (emitClear !== false) {
    emit('clear');
    showModal.value = false;
  }
}
// chamado pelo ChatList no onMounted -> restaura do storage e aplica.
// retorna true se aplicou algum filtro salvo.
function restoreAndApply() {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey.value) || 'null');
    const base = EMPTY();
    if (raw && typeof raw === 'object') {
      SECTION_KEYS.forEach(k => {
        const v = raw[k];
        if (Array.isArray(v)) base[k] = v;
        else if (v !== null && v !== undefined) base[k] = [v]; // formato antigo (single)
      });
      base.ia = raw.ia === true;
    }
    sel.value = base;
  } catch (e) { /* noop */ }
  return hasActiveFilters.value ? emitFor(sel.value) : false;
}

function onDocClick(e) {
  if (!showModal.value || modalMode.value !== 'popover') return;
  const target = e.target;
  if (popoverTriggerEl.value && popoverTriggerEl.value.contains(target)) return;
  if (popoverRef.value && popoverRef.value.contains(target)) return;
  closeModal();
}

function onDocKey(e) {
  if (e.key === 'Escape' && showModal.value) closeModal();
}

function onViewportChange() {
  if (showModal.value && modalMode.value === 'popover') {
    popoverStyle.value = computePopoverStyle(popoverTriggerEl.value);
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onDocKey);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true);
  document.removeEventListener('keydown', onDocKey);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
});

defineExpose({ clearAll, restoreAndApply, openModal });
</script>

<template>
  <div>
    <!-- ── Modal ─────────────────────────────────────────────────────── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showModal && modalMode === 'centered'"
          class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          @keydown.escape="closeModal"
        >
          <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="closeModal" />

          <div class="relative w-full max-w-md flex flex-col bg-n-alpha-2 dark:bg-n-solid-1 border border-n-strong rounded-2xl shadow-2xl overflow-visible">
            <!-- header -->
            <div class="flex items-center justify-between px-4 py-3 flex-shrink-0">
              <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
                <span class="i-lucide-filter size-4" /> Filtrar conversas
              </h3>
              <button
                type="button"
                class="p-1 rounded-lg hover:bg-n-alpha-2 text-n-slate-10"
                @click="closeModal"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>

            <!-- body -->
            <div class="flex flex-col px-3 pb-3 space-y-3">
              <!-- toggle "Apenas minhas equipes" (visual/simulado) -->
              <div class="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-n-solid-2 border border-n-weak shadow-sm">
                <span class="text-sm font-semibold text-n-slate-11">Apenas minhas equipes</span>
                <button
                  type="button"
                  class="relative w-10 h-6 rounded-full transition-colors flex-shrink-0"
                  :class="onlyMyTeams ? 'bg-n-blue-9' : 'bg-n-slate-6'"
                  role="switch"
                  :aria-checked="onlyMyTeams"
                  @click="onlyMyTeams = !onlyMyTeams"
                >
                  <span
                    class="absolute top-0.5 size-5 rounded-full bg-white shadow transition-all"
                    :class="onlyMyTeams ? 'ltr:left-[22px] rtl:right-[22px]' : 'ltr:left-0.5 rtl:right-0.5'"
                  />
                </button>
              </div>

              <!-- toggle "Atendidos pela IA" (FUNCIONAL — flag atendido_ia) -->
              <div
                v-if="showInboxFilter"
                class="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-n-solid-2 border border-n-weak shadow-sm"
              >
                <span class="text-sm font-semibold text-n-slate-11 flex items-center gap-2">
                  <span class="i-lucide-bot size-4.5 text-n-slate-11" />
                  Atendidos pela IA
                </span>
                <button
                  type="button"
                  class="relative w-10 h-6 rounded-full transition-colors flex-shrink-0"
                  :class="sel.ia ? 'bg-n-blue-9' : 'bg-n-slate-6'"
                  role="switch"
                  :aria-checked="sel.ia"
                  @click="toggleIa"
                >
                  <span
                    class="absolute top-0.5 size-5 rounded-full bg-white shadow transition-all"
                    :class="sel.ia ? 'ltr:left-[22px] rtl:right-[22px]' : 'ltr:left-0.5 rtl:right-0.5'"
                  />
                </button>
              </div>

              <!-- secoes (acordeao) -->
              <div
                v-for="f in FILTERS"
                :key="f.key"
                class="rounded-2xl bg-n-solid-2 border border-n-weak shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-3"
                  @click="toggleSection(f.key)"
                >
                  <span :class="[f.icon, 'size-5 text-n-slate-11 flex-shrink-0']" />
                  <span class="text-sm font-semibold text-n-slate-12">{{ f.label }}</span>
                  <span class="ml-auto flex items-center gap-2.5">
                    <template v-if="sel[f.key].length">
                      <span class="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-[50px] bg-n-blue-9 text-white text-xs font-bold">
                        {{ sel[f.key].length }}
                      </span>
                    </template>
                    <span
                      v-else
                      class="i-lucide-chevron-down size-5 text-n-slate-10 transition-transform"
                      :class="{ 'rotate-180': openKey === f.key }"
                    />
                  </span>
                </button>

                <div v-if="openKey === f.key" class="px-4 pb-3">
                  <div class="border-t border-n-weak mb-2" />

                  <div v-if="f.opts.length > 8" class="acl-filter-search mb-2">
                    <svg class="acl-filter-search__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                    <input
                      v-model="search[f.key]"
                      type="text"
                      :placeholder="`Buscar ${f.label.toLowerCase()}...`"
                      class="acl-filter-search__input"
                    >
                  </div>

                  <p v-if="!optsFor(f).length" class="text-xs text-n-slate-9 py-1">
                    Nenhum item disponível
                  </p>

                  <div
                    class="space-y-1"
                    :class="f.opts.length > 2 ? longOptionsListClass : ''"
                  >
                    <!-- Render agrupado (so na secao Canais) -->
                    <template v-if="f.groups && f.groups.length">
                      <template v-for="g in f.groups" :key="g.key">
                        <template v-if="groupItemsFor(f, g).length">
                          <div
                            v-if="nonEmptyGroupCount(f) > 1"
                            class="text-[11px] uppercase tracking-wider font-extrabold text-n-slate-12 px-1 pt-2.5 pb-1.5"
                          >
                            {{ g.title }}
                          </div>
                          <label
                            v-for="o in groupItemsFor(f, g)"
                            :key="o.id"
                            class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                          >
                            <input
                              type="checkbox"
                              class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                              :checked="isChecked(f.key, o.id)"
                              @change="toggleItem(f.key, o.id)"
                            />
                            <div class="flex-1 min-w-0 flex items-center gap-1.5">
                              <span class="truncate text-sm text-n-slate-11">{{ o.label }}</span>
                            </div>
                            <span
                              v-if="o.channel"
                              :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                              :style="{ color: o.channel.color }"
                            />
                          </label>
                        </template>
                      </template>
                    </template>

                    <!-- Render plano (etiquetas, usuarios, equipes) -->
                    <template v-else>
                      <label
                        v-for="o in optsFor(f)"
                        :key="o.id"
                        class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                      >
                        <input
                          type="checkbox"
                          class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                          :checked="isChecked(f.key, o.id)"
                          @change="toggleItem(f.key, o.id)"
                        />
                        <span class="flex-1 min-w-0 truncate text-sm text-n-slate-11">{{ o.label }}</span>
                        <span
                          v-if="o.color"
                          class="size-3 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: o.color }"
                        />
                        <span
                          v-else-if="o.channel"
                          :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                          :style="{ color: o.channel.color }"
                        />
                      </label>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ── Popover ─────────────────────────────────────────────────── -->
    <Teleport to="body">
      <Transition
        enter-active-class="acl-pill-filter-pop-enter-active"
        enter-from-class="acl-pill-filter-pop-from"
        enter-to-class="acl-pill-filter-pop-to"
        leave-active-class="acl-pill-filter-pop-leave-active"
        leave-from-class="acl-pill-filter-pop-to"
        leave-to-class="acl-pill-filter-pop-from"
      >
        <div
          v-if="showModal && modalMode === 'popover'"
          ref="popoverRef"
          class="fixed z-[9999] overflow-visible rounded-[10px] border border-[#e1e4e8] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.14)] acl-pill-filter-menu"
          :style="popoverStyle"
          @keydown.escape="closeModal"
        >
          <div class="space-y-3 px-3 py-3 acl-pill-filter-list">
            <div
              class="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-4 py-3 shadow-sm"
            >
              <span
                class="flex min-w-0 flex-1 items-center gap-3 text-sm font-semibold text-n-slate-11"
              >
                <span class="i-lucide-users size-5 flex-shrink-0" />
                <span>Apenas minhas equipes</span>
              </span>
              <button
                type="button"
                class="relative h-6 w-10 flex-shrink-0 rounded-full transition-colors"
                :class="onlyMyTeams ? 'bg-n-blue-9' : 'bg-n-slate-6'"
                role="switch"
                :aria-checked="onlyMyTeams"
                @click="onlyMyTeams = !onlyMyTeams"
              >
                <span
                  class="absolute top-0.5 size-5 rounded-full bg-white shadow transition-all"
                  :class="
                    onlyMyTeams
                      ? 'ltr:left-[22px] rtl:right-[22px]'
                      : 'ltr:left-0.5 rtl:right-0.5'
                  "
                />
              </button>
            </div>

            <div
              v-if="showInboxFilter"
              class="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-4 py-3 shadow-sm"
            >
              <span
                class="flex min-w-0 flex-1 items-center gap-3 text-sm font-semibold text-n-slate-11"
              >
                <span
                  class="i-lucide-bot size-5 flex-shrink-0 text-n-slate-11"
                />
                <span>Atendidos pela IA</span>
              </span>
              <button
                type="button"
                class="relative h-6 w-10 flex-shrink-0 rounded-full transition-colors"
                :class="sel.ia ? 'bg-n-blue-9' : 'bg-n-slate-6'"
                role="switch"
                :aria-checked="sel.ia"
                @click="toggleIa"
              >
                <span
                  class="absolute top-0.5 size-5 rounded-full bg-white shadow transition-all"
                  :class="
                    sel.ia
                      ? 'ltr:left-[22px] rtl:right-[22px]'
                      : 'ltr:left-0.5 rtl:right-0.5'
                  "
                />
              </button>
            </div>

            <div
              v-for="f in FILTERS"
              :key="f.key"
              class="overflow-hidden rounded-2xl border shadow-sm transition-colors"
              :class="
                openKey === f.key
                  ? 'border-[#b8d8c3] bg-[#f3faf5]'
                  : 'border-n-weak bg-n-solid-2'
              "
            >
              <button
                type="button"
                class="flex min-h-12 w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-[#f8fafc]"
                @click="toggleSection(f.key)"
              >
                <span
                  class="size-5 flex-shrink-0 text-n-slate-11"
                  :class="f.icon"
                />
                <span
                  class="flex-1 text-left text-sm font-semibold text-n-slate-12"
                >
                  {{ f.label }}
                </span>
                <span class="ml-auto flex items-center gap-2.5">
                  <template v-if="sel[f.key].length">
                    <span
                      class="inline-flex h-6 min-w-[26px] items-center justify-center rounded-[50px] bg-n-blue-9 px-1.5 text-xs font-bold text-white"
                    >
                      {{ sel[f.key].length }}
                    </span>
                  </template>
                  <span
                    v-else
                    class="i-lucide-chevron-down size-5 text-n-slate-10 transition-transform"
                    :class="{ 'rotate-180': openKey === f.key }"
                  />
                </span>
              </button>

                <div v-if="openKey === f.key" class="px-4 pb-3">
                  <div class="border-t border-n-weak mb-2" />

                  <div v-if="f.opts.length > 8" class="acl-filter-search mb-2">
                    <svg class="acl-filter-search__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                    <input
                      v-model="search[f.key]"
                      type="text"
                      :placeholder="`Buscar ${f.label.toLowerCase()}...`"
                      class="acl-filter-search__input"
                    >
                  </div>

                  <p v-if="!optsFor(f).length" class="text-xs text-n-slate-9 py-1">
                    Nenhum item disponível
                  </p>

                  <div
                    class="space-y-1"
                    :class="f.opts.length > 2 ? longOptionsListClass : ''"
                  >
                    <template v-if="f.groups && f.groups.length">
                      <template v-for="g in f.groups" :key="g.key">
                        <template v-if="groupItemsFor(f, g).length">
                          <div
                            v-if="nonEmptyGroupCount(f) > 1"
                            class="text-[11px] uppercase tracking-wider font-extrabold text-n-slate-12 px-1 pt-2.5 pb-1.5"
                          >
                            {{ g.title }}
                          </div>
                          <label
                            v-for="o in groupItemsFor(f, g)"
                            :key="o.id"
                            class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                          >
                            <input
                              type="checkbox"
                              class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                              :checked="isChecked(f.key, o.id)"
                              @change="toggleItem(f.key, o.id)"
                            />
                            <div class="flex-1 min-w-0 flex items-center gap-1.5">
                              <span class="truncate text-sm text-n-slate-11">{{ o.label }}</span>
                            </div>
                            <span
                              v-if="o.channel"
                              :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                              :style="{ color: o.channel.color }"
                            />
                          </label>
                        </template>
                      </template>
                    </template>

                    <template v-else>
                      <label
                        v-for="o in optsFor(f)"
                        :key="o.id"
                        class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                      >
                        <input
                          type="checkbox"
                          class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                          :checked="isChecked(f.key, o.id)"
                          @change="toggleItem(f.key, o.id)"
                        />
                        <span class="flex-1 min-w-0 truncate text-sm text-n-slate-11">{{ o.label }}</span>
                        <span
                          v-if="o.color"
                          class="size-3 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: o.color }"
                        />
                        <span
                          v-else-if="o.channel"
                          :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                          :style="{ color: o.channel.color }"
                        />
                      </label>
                  </template>
                </div>
              </div>
          </div>
        </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ── Slide panel (overlay total do ChatList) ──────────────────── -->
    <!-- Render in-place: o `relative` no root do ChatList ancora o absolute
         daqui. Slide-in pela esquerda (icone filter da pill bar); seta de
         voltar fecha com saida tambem pela esquerda. -->
    <Transition
      enter-active-class="acl-filter-slide-enter-active"
      enter-from-class="acl-filter-slide-from"
      enter-to-class="acl-filter-slide-to"
      leave-active-class="acl-filter-slide-leave-active"
      leave-from-class="acl-filter-slide-to"
      leave-to-class="acl-filter-slide-leave-to"
    >
        <div
          v-if="showModal && modalMode === 'slidePanel'"
          class="absolute inset-0 z-50 bg-white dark:bg-n-solid-1 flex flex-col"
          @keydown.escape="closeModal"
          tabindex="-1"
      >
        <!-- header sem borda em baixo -->
        <div class="flex items-center gap-3 px-4 py-3 flex-shrink-0">
          <button
            type="button"
            class="p-1.5 -ml-1.5 rounded-full hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
            aria-label="Voltar"
            @click="closeModal"
          >
            <span class="i-lucide-arrow-left size-5" />
          </button>
          <h3 class="text-lg font-bold text-n-slate-12">Filtros</h3>
        </div>

        <!-- body (mesmo conteudo do modal centered).
             flex-col + space-y-2.5: permite usar mt-auto no ultimo filho
             (botao Limpar filtros) pra empurra-lo pro final do container,
             mesmo quando a lista de secoes nao preenche o espaco. -->
        <div class="flex flex-col px-3 pt-[5px] pb-3 space-y-3">
          <div class="flex items-center justify-between gap-3 px-4 py-4 rounded-2xl bg-n-solid-2 shadow-[0_0_12px_rgba(0,0,0,0.10)] flex-shrink-0">
            <span class="text-sm font-semibold text-n-slate-11">Apenas minhas equipes</span>
            <button
              type="button"
              class="relative w-10 h-5 rounded-full transition-colors flex-shrink-0"
              :class="onlyMyTeams ? 'bg-n-blue-9' : 'bg-n-slate-6'"
              role="switch"
              :aria-checked="onlyMyTeams"
              @click="onlyMyTeams = !onlyMyTeams"
            >
              <span
                class="absolute top-0.5 size-4 rounded-full bg-white shadow transition-all"
                :class="onlyMyTeams ? 'ltr:left-[22px] rtl:right-[22px]' : 'ltr:left-0.5 rtl:right-0.5'"
              />
            </button>
          </div>

          <div
            v-if="showInboxFilter"
            class="flex items-center justify-between gap-3 px-4 py-4 rounded-2xl bg-n-solid-2 shadow-[0_0_12px_rgba(0,0,0,0.10)] flex-shrink-0"
          >
            <span class="text-sm font-semibold text-n-slate-11 flex items-center gap-2">
              <span class="i-lucide-bot size-4.5 text-n-slate-11" />
              Atendidos pela IA
            </span>
            <button
              type="button"
              class="relative w-10 h-5 rounded-full transition-colors flex-shrink-0"
              :class="sel.ia ? 'bg-n-blue-9' : 'bg-n-slate-6'"
              role="switch"
              :aria-checked="sel.ia"
              @click="toggleIa"
            >
              <span
                class="absolute top-0.5 size-4 rounded-full bg-white shadow transition-all"
                :class="sel.ia ? 'ltr:left-[22px] rtl:right-[22px]' : 'ltr:left-0.5 rtl:right-0.5'"
              />
            </button>
          </div>

          <div
            v-for="f in FILTERS"
            :key="f.key"
            class="rounded-2xl bg-n-solid-2 shadow-[0_0_12px_rgba(0,0,0,0.10)] overflow-hidden flex-shrink-0"
          >
            <button
              type="button"
              class="w-full flex items-center gap-3 px-4 py-4 transition-colors"
              :class="{ 'hover:bg-n-alpha-1': openKey !== f.key }"
              @click="toggleSection(f.key)"
            >
              <span :class="[f.icon, 'size-5 text-n-slate-11 flex-shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ f.label }}</span>
              <span class="ml-auto flex items-center gap-2.5">
                <template v-if="sel[f.key].length">
                  <span class="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-[50px] bg-n-blue-9 text-white text-xs font-bold">
                    {{ sel[f.key].length }}
                  </span>
                </template>
                <span
                  v-else
                  class="i-lucide-chevron-down size-5 text-n-slate-10 transition-transform"
                  :class="{ 'rotate-180': openKey === f.key }"
                />
              </span>
            </button>

            <div v-if="openKey === f.key" class="px-4 pb-3">
              <div class="border-t border-n-weak mb-2" />

              <div v-if="f.opts.length > 8" class="acl-filter-search mb-2">
                <svg class="acl-filter-search__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                <input
                  v-model="search[f.key]"
                  type="text"
                  :placeholder="`Buscar ${f.label.toLowerCase()}...`"
                  class="acl-filter-search__input"
                >
              </div>

              <p v-if="!optsFor(f).length" class="text-xs text-n-slate-9 py-1">
                Nenhum item disponível
              </p>

              <div
                class="space-y-1"
                :class="f.opts.length > 2 ? longOptionsListClass : ''"
              >
                <template v-if="f.groups && f.groups.length">
                  <template v-for="g in f.groups" :key="g.key">
                    <template v-if="groupItemsFor(f, g).length">
                      <div
                        v-if="nonEmptyGroupCount(f) > 1"
                        class="text-[11px] uppercase tracking-wider font-extrabold text-n-slate-12 px-1 pt-2.5 pb-1.5"
                      >
                        {{ g.title }}
                      </div>
                      <label
                        v-for="o in groupItemsFor(f, g)"
                        :key="o.id"
                        class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                      >
                        <input
                          type="checkbox"
                          class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                          :checked="isChecked(f.key, o.id)"
                          @change="toggleItem(f.key, o.id)"
                        />
                        <div class="flex-1 min-w-0 flex items-center gap-1.5">
                          <span class="truncate text-sm text-n-slate-11">{{ o.label }}</span>
                        </div>
                        <span
                          v-if="o.channel"
                          :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                          :style="{ color: o.channel.color }"
                        />
                      </label>
                    </template>
                  </template>
                </template>

                <template v-else>
                  <label
                    v-for="o in optsFor(f)"
                    :key="o.id"
                    class="flex items-center gap-2.5 py-2.5 cursor-pointer select-none rounded-lg px-2 hover:bg-n-alpha-1"
                  >
                    <input
                      type="checkbox"
                      class="size-4 rounded flex-shrink-0 accent-[#25D366] cursor-pointer"
                      :checked="isChecked(f.key, o.id)"
                      @change="toggleItem(f.key, o.id)"
                    />
                    <span class="flex-1 min-w-0 truncate text-sm text-n-slate-11">{{ o.label }}</span>
                    <span
                      v-if="o.color"
                      class="size-3 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: o.color }"
                    />
                    <span
                      v-else-if="o.channel"
                      :class="[o.channel.icon, 'size-5 flex-shrink-0']"
                      :style="{ color: o.channel.color }"
                    />
                  </label>
                </template>
              </div>
            </div>
          </div>
          <!-- Wrapper mt-auto empurra o botao pro fim do body; pt-6 garante
               margem visual entre o ultimo accordion e o botao mesmo quando
               a lista de secoes nao preenche o espaco. -->
          <div class="mt-auto pt-6">
            <button
              type="button"
              class="w-full px-4 py-3 rounded-xl bg-white text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-1 transition-colors flex items-center justify-center"
              style="border: 1px solid #b5b5b5;"
              @click="clearAll(true)"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Slide suave do painel de filtros.
   cubic-bezier(.22,.61,.36,1) = easeOutCubic, movimento natural sem rebote. */
.acl-pill-filter-pop-enter-active,
.acl-pill-filter-pop-leave-active {
  transition: opacity 140ms ease-out, transform 140ms ease-out;
  transform-origin: top left;
  will-change: opacity, transform;
}
.acl-pill-filter-pop-from {
  opacity: 0;
  transform: scale(0.96) translateX(8px);
}
.acl-pill-filter-pop-to {
  opacity: 1;
  transform: scale(1) translateX(0);
}
.acl-filter-slide-enter-active {
  transition: transform 380ms cubic-bezier(.22, .61, .36, 1),
              opacity 220ms ease-out;
  will-change: transform, opacity;
}
/* Leave é slide PURO (só transform, sem fade de opacidade): o painel permanece
   visível durante os 380ms inteiros do deslizar pra esquerda. Antes o opacity
   220ms < transform 380ms fazia o painel ficar invisível aos 220ms — o close
   parecia um fade rápido em vez de um deslize suave espelhando o open. */
.acl-filter-slide-leave-active {
  transition: transform 380ms cubic-bezier(.22, .61, .36, 1);
  will-change: transform;
}
.acl-filter-slide-from {
  transform: translateX(-100%);
  opacity: 0;
}
.acl-filter-slide-to {
  transform: translateX(0);
  opacity: 1;
}
/* leave-to próprio: desliza pra -100% MANTENDO opacity 1 (não herda o opacity:0
   do .acl-filter-slide-from, que é só do enter-from). Garante o slide visível. */
.acl-filter-slide-leave-to {
  transform: translateX(-100%);
}

/* Aceleres: checkbox customizado — fundo verde WhatsApp e check
   explicitamente branco (o accent-color nativo não garante contraste
   consistente entre navegadores). */
input[type="checkbox"].size-4 {
  appearance: none;
  -webkit-appearance: none;
  background-color: #ffffff;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  transition: background-color .12s, border-color .12s;
}
input[type="checkbox"].size-4:checked {
  background-color: #25D366;
  border-color: #25D366;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%23ffffff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='4 10 8 14 16 6'/%3E%3C/svg%3E");
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
}
input[type="checkbox"].size-4:focus-visible {
  outline: 2px solid rgba(37, 211, 102, 0.35);
  outline-offset: 1px;
}

.acl-pill-filter-menu {
  width: max-content;
  min-width: 320px;
}

@media (min-width: 1024px) {
  :global(
      #acl-pill-sort-popover.acl-pill-sort-menu.acl-pill-sort-menu--popover
    ) {
    left: 532.802px !important;
    right: auto !important;
  }
}

@media (max-width: 1023px) {
  :global(
      #acl-pill-sort-popover.acl-pill-sort-menu.acl-pill-sort-menu--popover
    ) {
    margin-left: 8px !important;
  }
}

:global(.dark) .acl-pill-filter-menu {
  background: #1f2123;
  border-color: #2c2f33;
}

/* Aceleres: listas de opções dos cards — scroll invisível (continua funcional). */
.acl-hide-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.acl-hide-scroll::-webkit-scrollbar { display: none; }

/* Aceleres: input de busca dentro dos cards de filtro (canais/etiquetas/usuários/
   equipes) — réplica fiel do .acl-search-pill do ChatList: pill sem borda visível,
   fundo translúcido cinza, anel inset cinza (#c4c4c4) no hover e verde (#1b8755)
   no focus. Visual/hover/select idênticos ao busca-rápido do topo. (2026-07-02) */
.acl-filter-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 14px;
  background: rgba(194, 189, 184, .15);
  border: none;
  border-radius: 50px;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow .12s;
}
.acl-filter-search:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}
.acl-filter-search:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}
.acl-filter-search__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}
.acl-filter-search__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 0;
  margin: 0;
  font-size: 13px;
  color: #1f2937;
  line-height: 40px;
}
.acl-filter-search__input::placeholder {
  color: #8a8a8a;
}
.acl-filter-search__input:focus,
.acl-filter-search__input:focus-visible,
.acl-filter-search__input:focus-within {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}
:global(.dark) .acl-filter-search {
  background: rgba(255, 255, 255, .04);
}
:global(.dark) .acl-filter-search:hover {
  box-shadow: inset 0 0 0 1px #3f3f46;
}
:global(.dark) .acl-filter-search__icon,
:global(.dark) .acl-filter-search__input::placeholder {
  color: #a1a1aa;
}
:global(.dark) .acl-filter-search__input {
  color: #e5e5e5;
}
</style>
